import { t } from "@lingui/macro";
import { createId } from "@paralleldrive/cuid2";
import type { CoverLetterDto } from "@reactive-resume/dto";
import { defaultCoverLetterLayout, defaultCustomSection, defaultSection, insertBeforeClosingInPlace, type CustomSectionGroup } from "@reactive-resume/schema";
import { removeItemInLayout } from "@reactive-resume/utils";
import _set from "lodash.set";
import type { TemporalState } from "zundo";
import { temporal } from "zundo";
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { useStoreWithEqualityFn } from "zustand/traditional";

import { debouncedUpdateCoverLetter } from "../services/cover-letter";
import { useSaveStatusStore } from "./useSaveStatusStore";

type CoverLetterStore = {
  coverLetter: CoverLetterDto;
  setValue: (path: string, value: unknown) => void;
  addSection: () => void;
  removeSection: (sectionId: string) => void;
};

export const useCoverLetterStore = create<CoverLetterStore>()(
  temporal(
    immer((set) => ({
      coverLetter: {} as CoverLetterDto,
      setValue: (path, value) => {
        set((state) => {
          if (path === "visibility") {
            state.coverLetter.visibility = value as "public" | "private";
          } else {
            state.coverLetter.data = _set(state.coverLetter.data, path, value);
          }

          useSaveStatusStore.getState().setStatus("saving");
          void debouncedUpdateCoverLetter(JSON.parse(JSON.stringify(state.coverLetter)));
        });
      },
      addSection: () => {
        const section: CustomSectionGroup = {
          ...defaultSection,
          canAICustomize: true,
          id: createId(),
          name: t`Custom Section`,
          content: "",
          items: [
            {
              ...defaultCustomSection,
              id: createId(),
              name: "",
            },
          ],
        };

        set((state) => {
          if (!state.coverLetter.data.metadata.layout) {
            state.coverLetter.data.metadata.layout = JSON.parse(JSON.stringify(defaultCoverLetterLayout));
          }
          if (!state.coverLetter.data.custom) {
            state.coverLetter.data.custom = {};
          }
          const lastPageIndex = state.coverLetter.data.metadata.layout.length - 1;
          insertBeforeClosingInPlace(
            state.coverLetter.data.metadata.layout[lastPageIndex][0],
            `custom.${section.id}`,
          );
          state.coverLetter.data = _set(state.coverLetter.data, `custom.${section.id}`, section);

          useSaveStatusStore.getState().setStatus("saving");
          void debouncedUpdateCoverLetter(JSON.parse(JSON.stringify(state.coverLetter)));
        });
      },
      removeSection: (sectionId: string) => {
        if (sectionId.startsWith("custom.")) {
          const id = sectionId.split("custom.")[1];

          set((state) => {
            if (state.coverLetter.data.metadata.layout) {
              removeItemInLayout(sectionId, state.coverLetter.data.metadata.layout);
            }
            if (state.coverLetter.data.custom) {
              delete state.coverLetter.data.custom[id];
            }

            useSaveStatusStore.getState().setStatus("saving");
            void debouncedUpdateCoverLetter(JSON.parse(JSON.stringify(state.coverLetter)));
          });
        }
      },
    })),
    {
      limit: 100,
      wrapTemporal: (fn) => devtools(fn),
      partialize: ({ coverLetter }) => ({ coverLetter }),
    },
  ),
);

export const useTemporalCoverLetterStore = <T>(
  selector: (state: TemporalState<Pick<CoverLetterStore, "coverLetter">>) => T,
  equality?: (a: T, b: T) => boolean,
) => useStoreWithEqualityFn(useCoverLetterStore.temporal, selector, equality);
